import { useCallback, useEffect, useState } from "react";

import EditNameModal from "./EditName/EditName";
import EditPasswordModal from "./EditPassword/EditPassword";
import EditSymbolMultiple from "./EditSymbolMultiple/EditSymbolMultiple";
import EditCommission from "./EditCommission/EditCommission";
import EditSharing from "./EditSharing/EditSharing";
import EditDollarRate from "./EditDollarRate/EditDollarRate";
import EditInterestSharing from "./EditInterestSharing/EditInterestSharing";

import useSize from "../../useSize/useSize";
import StatusButton from "./StatusButton/StatusButton";
import ResetPassword from "./StatusButton/ResetPassword";
import ActionButton from "./ActionButton/ActionButton";
import toast from "react-hot-toast";
import Backend from "../../../Backend/Backend";
import Loading from "../../Loading/Loading";

const backend = await new Backend().init();

export default function Edit({ user_id }) {
  const windowSize = useSize();

  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState({});
  const [loading, setLoading] = useState(true);

  // Modals
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isSymbolMultipleModalOpen, setIsSymbolMultipleModalOpen] =
    useState(false);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [isDollarModalOpen, setIsDollarModalOpen] = useState(false);
  const [isInterestSharingModalOpen, setIsInterestSharingModalOpen] =
    useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const closeAllModals = () => {
    setIsNameModalOpen(false);
    setIsPasswordModalOpen(false);
    setIsSymbolMultipleModalOpen(false);
    setIsCommissionModalOpen(false);
    setIsSharingModalOpen(false);
    setIsInterestSharingModalOpen(false);
    setIsDollarModalOpen(false);
  };

  /** --------------------------
   * LOAD USER DATA
   ---------------------------*/
  const loadUser = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        user_id,
      };

      const r = await backend.load_user_select(payload);
      if (r?.error === "False") {
        setUser(r.user || {});
        setAdmin(r.userdata || {});
        setLoading(false);
      } else {
        setLoading(false);
        toast.error(r?.message || "Failed to load user");
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      toast.error("Error loading user");
    }
  }, [user_id]);

  useEffect(() => {
    if (user_id) loadUser();
  }, [user_id, loadUser]);

  // ESC to close modals
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") closeAllModals();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  const containerHeight = windowSize[1] - 170;

  /** --------------------------
   * WHEN USER ID IS MISSING
   ---------------------------*/
  if (!user_id) {
    return (
      <div className="p-6 m-4 bg-red-50 border border-red-200 rounded-xl text-center shadow">
        <h2 className="text-red-600 font-bold text-lg">⚠️ Error</h2>
        <p>User ID is required.</p>
      </div>
    );
  }

  /** --------------------------
   * LOADING STATE
   ---------------------------*/
  if (loading || !user) {
    return (
      <div className="loadingContainer">
        <Loading height={containerHeight} />
      </div>
    );
  }

  /** --------------------------
   * MAIN UI
   ---------------------------*/

  const {
    id,
    broker_id,
    sub_broker_id,
    name,
    username,
    trade,
    sharing,
    nse_cash,
    fresh_limit,
    only_sq,
    intraday_sq,
    auto_sq,
    status,
    symbol_multiple,
    dollar_rate,
    deposit_interest_rate,
    rupee_dollar,
  } = user;

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 pl-5 pr-5 pb-5"
      style={{ height: containerHeight, width: windowSize[0] - 100 }}
    >
      {/* ------------------------------------
          COLUMN 1 — USER DETAILS
      --------------------------------------*/}
      <div
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
        className="bg-[var(--background)] rounded-xl p-5 pb-0 shadow-inner border-2 border-[var(--hover)]"
      >
        <div className="space-y-2 text-sm">
          <DetailRow label="USERNAME" value={username} />

          <DetailRow
            label="NAME"
            value={name}
            buttonLabel="EDIT"
            onButtonClick={() => setIsNameModalOpen(true)}
          />

          <DetailRow
            label="PASSWORD"
            value="********"
            buttonLabel="EDIT"
            onButtonClick={() => setIsPasswordModalOpen(true)}
          />

          <DetailRow
            label="SYMBOL MULTIPLY"
            value={symbol_multiple}
            buttonLabel="EDIT"
            onButtonClick={() => setIsSymbolMultipleModalOpen(true)}
          />

          <DetailRow
            label="SHARING"
            value={sharing}
            buttonLabel="EDIT"
            onButtonClick={() => setIsSharingModalOpen(true)}
          />

          {intraday_sq ? (
            <DetailRow
              label="DOLLAR RATE"
              value={`${dollar_rate}`}
              buttonLabel="EDIT"
              onButtonClick={() => setIsDollarModalOpen(true)}
            />
          ) : null}
          {nse_cash ? (
            <DetailRow
              label="INTEREST SHARING"
              value={`${deposit_interest_rate} %`}
              buttonLabel="EDIT"
              onButtonClick={() => setIsInterestSharingModalOpen(true)}
            />
          ) : null}
        </div>

        {/* USER STATUS BUTTONS */}
        <div className="flex flex-wrap gap-2 mt-5">
          <ResetPassword user_id={id} />
          {[
            ["ACTIVE", "status", status],
            ["TRADE", "trade", trade],
            ["FRESH LIMIT", "fresh_limit", fresh_limit],
            ["ONLY SQ", "only_sq", only_sq],
            ["AUTO SQ", "auto_sq", auto_sq],
            // ["INTRADAY SQ", "intraday_sq", intraday_sq],
            // ["WEEKLY SQ", "week_sq", week_sq],
            // ["WEEKLY SETTLEMENT", "week_settlement", week_settlement],
            // ["RESET DEPOSIT", "reset_deposit", reset_deposit],
          ].map(([label, key, val]) => (
            <StatusButton
              key={key}
              label={label}
              user_id={user_id}
              user_label={key}
              user_status={val}
            />
          ))}
        </div>
      </div>

      {/* ------------------------------------
          COLUMN 2 — USER EXCHANGES + SETTINGS
      --------------------------------------*/}
      <div
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
        className="bg-[var(--background)] rounded-xl p-5 pb-0  shadow-inner border-2 border-[var(--hover)]"
      >
        <h3 className="font-bold text-[var(--text)] text-lg mb-3">
          🌐 User Exchanges
        </h3>

        <SectionChips
          reload={loadUser}
          user={user}
          chips={[
            { key: "nse", label: "Nse Future", status: admin.nse },
            { key: "nse_cash", label: "Nse Equity", status: admin.nse_cash },
            { key: "options", label: "Nse Options", status: admin.options },
            { key: "mcx", label: "Mcx Future", status: admin.mcx },
            { key: "mcx_options", label: "Mcx Options", status: admin.mcx },
            {
              key: "comex",
              label: "COMEX",
              status: admin.comex,
            },
            {
              key: "crypto",
              label: "Crypto",
              status: admin.crypto,
            },
            {
              key: "forex",
              label: "Forex",
              status: admin.forex,
            },
            { key: "sgx", label: "SGX", status: admin.sgx },
            { key: "us_stock", label: "US Stock", status: admin.us_stock },
            { key: "index", label: "Others", status: admin.index },
            { key: "dgcx", label: "Dgcx", status: admin.dgcx },
            { key: "ncdex", label: "Ncdex", status: admin.ncdex },
          ]}
        />

        <h3 className="font-bold text-lg text-[var(--text)] mt-5 mb-3">
          ⚙️ User Other
        </h3>

        {[
          ["SQ-OFF TIME", "sq_time"],
          ["LIMIT %", "limit_p"],
          ["PROFIT LIMIT", "profit_limit"],
          ["STANDING LIMIT", "standing_limit"],
          ["MIN RATE", "min_rate"],
          ["TOTAL MCX LOT", "max_lot"],
        ].map(([label, key]) => (
          <div
            key={key}
            className="flex justify-between items-center border-b-2 border-[var(--hover)] pb-1 text-sm"
          >
            <span className="font-bold  bg-[var(--background)] text-[var(--text)] ">
              {label}: {user[key]}
            </span>

            <ActionButton
              label="SUBMIT"
              item={key}
              user={user}
              reload={loadUser}
            />
          </div>
        ))}
      </div>

      {/* ------------------------------------
          COLUMN 3 — EXCHANGE SETTINGS
      --------------------------------------*/}
      <div
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
        className="bg-[var(--background)] rounded-xl p-5 pb-0  shadow-inner border-2 border-[var(--hover)]"
      >
        <h3 className="font-bold text-lg text-[var(--text)] mb-3">
          ⚡ Exchange Settings
        </h3>

        <div className="space-y-6">
          {[
            ["nse", "📈 NSE Future"],
            ["nse_cash", "📈 NSE Equity"],
            ["options", "📝 NSE Options"],
            ["mcx", "💹 MCX Future"],
            ["mcx_options", "💹 MCX Options"],
            ["comex", "🌍 COMEX"],
            ["crypto", "₿ Crypto"],
            ["forex", "💱 Forex"],
            ["us_stock", "📈 US Stock"],
            ["sgx", "📈 Sgx"],
            ["index", "🇺🇸 Others"],
            ["dgcx", "🇺🇸 Dgcx"],
            ["ncdex", "🇺🇸 Ncdex"],
          ].map(([key, label]) => {
            if (user[key] !== 1) return null;

            const multipleKey = `symbol_multiple_${key}`;
            const limitKey = `${key}_limit`;
            const midLimitKey = `${key}_mid_limit`;
            const marginKey = `margin_${key}`;
            const marginHoldKey = `margin_${key}_carry`;
            const commissionKey =
              key === "mcx"
                ? user.lot_volume === 0
                  ? `b_${key}_lot`
                  : `b_${key}_volume`
                : `b_${key}`;
            const brokerKey = `bb_${key}`;
            const subbrokerKey = `sb_${key}`;
            const type =
              key === "nse" ||
              key === "nse_cash" ||
              key === "sgx" ||
              key === "us_stock" ||
              key === "index"
                ? `V`
                : "L";

            return (
              <div
                key={key}
                className="space-y-3 border-b border-[var(--hover)] pb-4"
              >
                <div className="flex justify-between items-center">
                  <h4 className="font-bold text-[var(--text)]">{label}</h4>

                  <button
                    className="px-3 py-1 bg-[var(--primary)] text-white rounded-full text-xs font-semibold"
                    onClick={() => {
                      setSelectedType({ key, title: label });
                      setIsCommissionModalOpen(true);
                    }}
                  >
                    EDIT
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-10">
                  <StatusButton
                    label="LIMIT"
                    user_id={id}
                    user_label={limitKey}
                    user_status={user[limitKey]}
                  />
                  <div />
                  <StatusButton
                    label="MID LIMIT"
                    user_id={id}
                    user_label={midLimitKey}
                    user_status={user[midLimitKey]}
                  />
                </div>

                <EditRow
                  multipleKey={user[multipleKey]}
                  exchange={key}
                  user={user}
                  label1={user[marginKey]}
                  label2={user[marginHoldKey]}
                  label3={user[commissionKey]}
                  label4={user[brokerKey]}
                  label5={user[subbrokerKey]}
                  broker_id={broker_id}
                  sub_broker_id={sub_broker_id}
                  type={type}
                  rupee_dollar={rupee_dollar}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* ------------------------------------
          MODALS
      --------------------------------------*/}
      <EditNameModal
        open={isNameModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditPasswordModal
        open={isPasswordModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditSymbolMultiple
        open={isSymbolMultipleModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditSharing
        open={isSharingModalOpen}
        onClose={closeAllModals}
        user={user}
        admin={admin}
        reload={loadUser}
      />
      <EditInterestSharing
        open={isInterestSharingModalOpen}
        onClose={closeAllModals}
        user={user}
        admin={admin}
        reload={loadUser}
      />
      <EditDollarRate
        open={isDollarModalOpen}
        onClose={closeAllModals}
        user={user}
        admin={admin}
        reload={loadUser}
      />
      <EditCommission
        open={isCommissionModalOpen}
        onClose={closeAllModals}
        user={user}
        admin={admin}
        type={selectedType}
        fetchUsers={loadUser}
      />
    </div>
  );
}

/* -------------------------------------------------- */
/* COMPONENTS */
/* -------------------------------------------------- */

function DetailRow({ label, value, buttonLabel, onButtonClick }) {
  return (
    <div className="flex justify-between border-b-2 border-[var(--hover)] pb-1 items-center">
      <span className="font-bold text-[var(--text)] ">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-bold text-[var(--text)] ">{value}</span>

        {buttonLabel && (
          <button
            onClick={onButtonClick}
            className="px-2 py-0.5 bg-[var(--primary)] text-white rounded-full text-xs font-semibold"
          >
            {buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function SectionChips({ chips, user, reload }) {
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) =>
        chip.status === 1 ? (
          <StatusButton
            key={chip.key}
            label={chip.label}
            user_id={user.id}
            user_label={chip.key}
            user_status={user[chip.key]}
            fetchUsers={reload}
            reload={1}
          />
        ) : null,
      )}
    </div>
  );
}

function EditRow({
  multipleKey,
  exchange,
  user,
  label1,
  label2,
  label3,
  label4, 
  label5,
  broker_id,
  sub_broker_id,
  type,
  rupee_dollar,
}) {
  const isDollar =
    exchange === "comex" ||
    exchange === "crypto" ||
    exchange === "forex" ||
    exchange === "us_stock" ||
    exchange === "dgcx" ||
    exchange === "index";

  // Determine grid classes based on broker_id and sub_broker_id
  const getGridClasses = () => {
    const hasBroker = broker_id !== 0;
    const hasSubBroker = sub_broker_id !== 0;

    if (hasBroker && hasSubBroker) {
      return user.intraday_sq === 1 ? "grid-cols-6 gap-6" : "grid-cols-5 gap-5";
    } else if (hasBroker) {
      return user.intraday_sq === 1 ? "grid-cols-5 gap-5" : "grid-cols-4 gap-4";
    } else if (hasSubBroker) {
      return user.intraday_sq === 1 ? "grid-cols-5 gap-5" : "grid-cols-4 gap-4"; // If only sub_broker exists, still use 4 columns
    } else if (exchange === "mcx") {
      return user.intraday_sq === 1 ? "grid-cols-5 gap-5" : "grid-cols-4 gap-4";
    } else {
      return user.intraday_sq === 1 ? "grid-cols-4 gap-4" : "grid-cols-3 gap-3";
    }
  };

  const gridClasses = getGridClasses();

  return (
    <div className={`grid ${gridClasses} text-xs font-semibold`}>
      <div>
        <span className="text-[var(--subtext)] text-[10px]">MULTIPLE</span>
        <div className="font-bold text-[var(--text)]">{multipleKey || 0}</div>
      </div>
      <div>
        <span className="text-[var(--subtext)] text-[10px]">MARGIN</span>
        <div className="font-bold text-[var(--text)]">{label1}</div>
      </div>
      {user.intraday_sq === 1 ? (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">MARGIN-H</span>
          <div className="font-bold text-[var(--text)]">{label2}</div>
        </div>
      ) : null}

      {exchange === "mcx" ? (
        <>
          <div>
            <span className="text-[var(--subtext)] text-[10px]">COMM-LOT</span>
            <div className="font-bold text-[var(--text)]">
              {rupee_dollar === 0 ? "₹" : isDollar ? "$" : "₹"} {user.b_mcx_lot}{" "}
              L
            </div>
          </div>
          <div>
            <span className="text-[var(--subtext)] text-[10px]">COMM-VOL</span>
            <div className="font-bold text-[var(--text)]">
              {rupee_dollar === 0 ? "₹" : isDollar ? "$" : "₹"} {user.b_mcx_volume} V
            </div>
          </div>
        </>
      ) : (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">COMMISSION</span>
          <div className="font-bold text-[var(--text)]">
            {rupee_dollar === 0 ? "₹" : isDollar ? "$" : "₹"} {label3} {type}
          </div>
        </div>
      )}

      {broker_id !== 0 && (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">BROKER</span>
          <div className="font-bold text-[var(--text)]">
            {exchange === "mcx"
              ? user.lot_volume === 0
                ? label4
                : parseFloat(label4)
              : label4}{" "}
            {type}
          </div>
        </div>
      )}

      {sub_broker_id !== 0 && (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">SUB BROKER</span>
          <div className="font-bold text-[var(--text)]">
            {label5} {type}
          </div>
        </div>
      )}
    </div>
  );
}
